import React, { useState } from 'react';
import Modal from '../../../BaseComponents/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { Formik } from 'formik';
import TextArea from '../../../BaseComponents/TextArea';
import './FoodModal.scss';
import Button from '../../../BaseComponents/Button';
import TextInput from '../../../BaseComponents/TextInput';
import { CreateFoodRequest } from '../../../../types/apiGeneratedTypes';
import { createFood, updateFood } from '../../../../api/food';
import {
  changeDeleteFoodModalVisibility,
  setFoodToDelete,
  changeModalVisibility,
  setFoodsList
} from '../../../../store/foodsSlice';
import { ReactComponent as BinIcon } from '../../../../assets/icons/TrashBin2.svg';
import UploadPhoto from '../../../UploadPhoto';
import { toast } from 'react-toastify';
import { getFoodList } from '../../../../api/food';

type FoodsModalProps = {
  isOpen: boolean;
};

interface FoodFormValues {
  name: string;
  description: string;
  photo: string;
}

const FoodsModal: React.FC<FoodsModalProps> = ({ isOpen }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const { foodToEdit } = useSelector((state: RootState) => state.foods);

  const getDefaultValues = (): FoodFormValues => {
    if (foodToEdit) {
      return {
        name: foodToEdit.name ?? '',
        description: foodToEdit.description ?? '',
        photo: foodToEdit.image ?? ''
      };
    }
    return {
      name: '',
      description: '',
      photo: ''
    };
  };

  const defaultValues = getDefaultValues();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => dispatch(changeModalVisibility(false))}
      title={'Add Food'}
      width={'800px'}
    >
      <Formik
        initialValues={defaultValues}
        onSubmit={async (values) => {
          setIsLoading(true);
          try {
            const requestObject: CreateFoodRequest = {
              name: values.name,
              description: values.description,
              image: values.photo
            };

            if (foodToEdit) {
              await updateFood({
                ...requestObject,
                id: foodToEdit?.id as number
              });
              toast.success('Food updated successfully');
            } else {
              await createFood(requestObject);
              toast.success('Food added successfully');
            }
            dispatch(changeModalVisibility(false));
            const updatedList = await getFoodList();
            if (updatedList) {
              dispatch(setFoodsList(updatedList));
            }
          } catch (error) {
            setError(error);
            toast.error('Error adding or updating food');
          } finally {
            setIsLoading(false);
          }
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => (
          <form onSubmit={handleSubmit} className='food-modal-form'>
            <div className='upload-photo-wrapper'>
              <UploadPhoto
                defaultPhoto={foodToEdit?.image_url!}
                onPhotoChange={(photo) => setFieldValue('image', photo)}
                putToStaticFolder={true}
              />
            </div>
            <TextInput
              name='name'
              title='Name *'
              placeholder='Enter name'
              value={values.name}
              onChange={(e) => setFieldValue('name', e.target.value)}
              isError={false}
            />
            <TextArea
              name='description'
              title='Description *'
              placeholder='Enter description'
              value={values.description}
              onChange={(e) => setFieldValue('description', e.target.value)}
              isError={false}
            />
            <div className='btn-wrapper'>
              <Button
                text={foodToEdit ? 'Save changes' : 'Add Food'}
                type='submit'
                isDisabled={
                  !values.name  || !values.photo
                }
                isLoading={isLoading}
              />
              {foodToEdit && (
                <div
                  className='delete-btn'
                  onClick={() => {
                    dispatch(setFoodToDelete(foodToEdit));
                    dispatch(changeDeleteFoodModalVisibility(true));
                  }}
                >
                  <BinIcon />
                  Delete Food
                </div>
              )}
              {error && (
                <div className='error-message'>
                  Something went wrong. Please try again.
                </div>
              )}
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default FoodsModal;
